'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { DEFAULT_AUTOSHIP_CADENCE_VALUE, getAutoshipCadenceByValue, type Product } from '@libs/common-types';

import { resolveFulfillmentMethod } from '../../product-details';
import { isMethodAvailableForPurchaseOption } from '../lib';
import { miniPdpFormSchema } from '../schema';
import type { MiniPdpForm } from '../types';

const defaultCadence = getAutoshipCadenceByValue(DEFAULT_AUTOSHIP_CADENCE_VALUE);

if (!defaultCadence) {
    throw new Error('Default autoship cadence is not supported');
}

const getDefaultValues = (product?: Product, selectedSku = ''): MiniPdpForm => {
    const availableMethods = {
        bopis: Boolean(product && isMethodAvailableForPurchaseOption(product, 'BOPIS', 'one-time')),
        sdd: Boolean(product && isMethodAvailableForPurchaseOption(product, 'SDD', 'one-time')),
        sth: Boolean(product && isMethodAvailableForPurchaseOption(product, 'STH', 'one-time')),
    };
    const fulfilmentMethod = resolveFulfillmentMethod({ availableMethods });
    const method = fulfilmentMethod.toUpperCase() as keyof Product['deliveryMethods'];

    return {
        selectedSku,
        fulfilmentMethod,
        quantity: product?.deliveryMethods[method].purchaseQuantities?.min ?? 1,
        purchaseOption: 'one-time',
        subscriptionType: 'new',
        autoshipFrequency: defaultCadence.frequency,
        autoshipUnit: defaultCadence.unitOfMeasure,
    };
};

export const useMiniPdpForm = (product?: Product, selectedSku = '') => {
    const form = useForm<MiniPdpForm>({
        defaultValues: getDefaultValues(product, selectedSku),
        resolver: zodResolver(miniPdpFormSchema),
    });

    useEffect(() => {
        form.reset(getDefaultValues(product, selectedSku));
        void form.trigger();
    }, [form, product, selectedSku]);

    return form;
};
